import { maybe } from '@passionware/monads';
import { appRouting } from 'v5/core/routing/appRouting';
import { myRouting } from 'v5/features/core/routing';
import type { EnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { adaptLegacyWorkspaceRoutingForLinks } from './adaptLegacyWorkspaceRoutingForLinks';

/**
 * Entity home: links to a foreign entity (a 1:1 entity whose home is not the
 * browsing scope — the browsing scope is wider, or a by-id reference to another
 * scope), public URLs, share / slug prefixes. Reads `enterprise_workspace_id`
 * (and `team_id`) from the entity record, never the browsing scope or the
 * `enterprise` flag. A contact's name links in the browsing scope
 * (`useBrowsingWorkspaceRoutes`); here only its membership chips.
 *
 * @see .agents/rules/workspace-scoping-layers.md
 */

export type WorkspaceRef = { id: number; slug: string };

export type TeamRef = { id: number; slug: string };

export type WorkspaceScopedEntity = {
  enterprise_workspace_id?: number | null;
};

export type TeamScopedEntity = WorkspaceScopedEntity & {
  enterprise_team_id?: number | null;
};

export function getSharedEnterpriseWorkspaceId(
  entities: WorkspaceScopedEntity[]
): number | null {
  if (entities.length === 0) {
    return null;
  }

  const workspaceIds = entities.map(
    entity => entity.enterprise_workspace_id ?? null
  );
  const definedIds = [
    ...new Set(workspaceIds.filter((id): id is number => id != null))
  ];

  if (definedIds.length === 0) {
    return null;
  }

  if (definedIds.length > 1) {
    return null;
  }

  const sharedWorkspaceId = definedIds[0]!;
  if (workspaceIds.some(id => id != null && id !== sharedWorkspaceId)) {
    return null;
  }

  if (workspaceIds.some(id => id === null)) {
    return null;
  }

  return sharedWorkspaceId;
}

export function resolveEntityWorkspaceSlug(
  entity: WorkspaceScopedEntity,
  workspaces: WorkspaceRef[] | undefined
): string | undefined {
  if (entity.enterprise_workspace_id == null || !workspaces?.length) {
    return undefined;
  }

  return workspaces.find(
    workspace => workspace.id === entity.enterprise_workspace_id
  )?.slug;
}

export function buildEntityWorkspaceRoutes(
  organizationSlug: string,
  entity: WorkspaceScopedEntity,
  workspaces: WorkspaceRef[] | undefined
) {
  const workspaceSlug = resolveEntityWorkspaceSlug(entity, workspaces);

  if (workspaceSlug) {
    return appRouting.org(organizationSlug).workspace(workspaceSlug);
  }

  return adaptLegacyWorkspaceRoutingForLinks(
    myRouting.forWorkspace(organizationSlug)
  );
}

export function getContactDetailPath(
  organizationSlug: string,
  entity: WorkspaceScopedEntity,
  workspaces: WorkspaceRef[] | undefined,
  contactId: string | number
): string {
  return buildEntityWorkspaceRoutes(
    organizationSlug,
    entity,
    workspaces
  ).contacts.detail(contactId);
}

export function getCalloutDetailPath(
  organizationSlug: string,
  callout: WorkspaceScopedEntity & { id: number },
  workspaces: WorkspaceRef[] | undefined
): string {
  return buildEntityWorkspaceRoutes(
    organizationSlug,
    callout,
    workspaces
  ).callouts.detail(callout.id);
}

export function resolveEntityTeamSlug(
  entity: TeamScopedEntity,
  teams: TeamRef[] | undefined
): string | undefined {
  if (entity.enterprise_team_id == null || !teams?.length) {
    return undefined;
  }

  return teams.find(team => team.id === entity.enterprise_team_id)?.slug;
}

/**
 * Detail path for a production from **entity assignment** (layer 3).
 *
 * For **list → open this production** (and similar entry points). Do not use for
 * sibling tabs / breadcrumbs while already on a production detail URL — use
 * `useAppProductionsRouting` instead.
 *
 * Resolves `enterprise_workspace_id` / `enterprise_team_id` via `workspaces` / `teams`
 * catalogs — same contract as {@link getShortlistDetailPath}.
 * Team URL when both slugs resolve, else legacy. No workspace-without-team home.
 */
export function getProductionDetailPath(
  organizationSlug: string,
  production: TeamScopedEntity & { id: number },
  workspaces: WorkspaceRef[] | undefined,
  teams?: TeamRef[] | undefined
): string {
  const workspaceSlug = resolveEntityWorkspaceSlug(production, workspaces);
  const teamSlug = resolveEntityTeamSlug(production, teams);

  if (workspaceSlug && teamSlug) {
    return appRouting
      .org(organizationSlug)
      .workspace(workspaceSlug)
      .team(teamSlug)
      .productions.detail(production.id);
  }

  return myRouting
    .forWorkspace(organizationSlug)
    .productions()
    .detail(production.id);
}

/**
 * Detail path for a shortlist from **entity assignment** (layer 3).
 *
 * For **list → open this shortlist** (and similar entry points). Do not use for
 * in-page chrome while already on a shortlist detail URL — use browsing-scope
 * routing (`useAppShortlistsRouting` / slug app scope) instead.
 *
 * Uses `shortlist.enterprise_workspace_id` + `enterprise_team_id` and resolves those
 * numeric ids to URL slugs via the `workspaces` / `teams` catalogs. Callers must pass
 * catalogs that can contain the entity’s workspace/team (org list → fan-out; team list →
 * that workspace’s teams). Until both slugs resolve, returns the legacy
 * `/app/{org}/shortlists/{id}` path — so prefer not rendering the link (or not fetching
 * rows) until those catalogs are success if a wrong/legacy href is unacceptable.
 *
 * Same shape as productions / callsheets (no workspace-without-team home).
 *
 * @see .agents/rules/workspace-scoping-layers.md
 */
export function getShortlistDetailPath(
  organizationSlug: string,
  shortlist: TeamScopedEntity & { id: number },
  workspaces: WorkspaceRef[] | undefined,
  teams?: TeamRef[] | undefined
): string {
  const workspaceSlug = resolveEntityWorkspaceSlug(shortlist, workspaces);
  const teamSlug = resolveEntityTeamSlug(shortlist, teams);

  if (workspaceSlug && teamSlug) {
    return appRouting
      .org(organizationSlug)
      .workspace(workspaceSlug)
      .team(teamSlug)
      .shortlists.detail(shortlist.id);
  }

  return myRouting
    .forWorkspace(organizationSlug)
    .shortlists()
    .detail(shortlist.id);
}

type TeamScopedDetailPathDeps = {
  workspaces?: WorkspaceRef[];
  enterpriseService: Pick<EnterpriseService, 'ensureWorkspace' | 'ensureTeam'>;
};

async function resolveEntityWorkspaceAndTeamSlugs(
  organizationId: string,
  entity: TeamScopedEntity,
  workspaces: WorkspaceRef[] | undefined,
  enterpriseService: TeamScopedDetailPathDeps['enterpriseService']
): Promise<{ workspaceSlug?: string; teamSlug?: string }> {
  let workspaceSlug = resolveEntityWorkspaceSlug(entity, workspaces);
  if (!workspaceSlug && entity.enterprise_workspace_id != null) {
    const workspace = await enterpriseService.ensureWorkspace(
      organizationId,
      entity.enterprise_workspace_id
    );
    workspaceSlug = maybe.flatMapOrElse(workspace, w => w.slug, undefined);
  }

  let teamSlug: string | undefined;
  if (
    entity.enterprise_team_id != null &&
    entity.enterprise_workspace_id != null
  ) {
    const team = await enterpriseService.ensureTeam(
      organizationId,
      entity.enterprise_workspace_id,
      entity.enterprise_team_id
    );
    teamSlug = maybe.flatMapOrElse(team, t => t.slug, undefined);
  }

  return { workspaceSlug, teamSlug };
}

/** Layer 3 detail path — resolves workspace/team slugs via enterprise service when refs are missing. */
export async function getShortlistDetailPathAsync(
  organizationSlug: string,
  shortlist: TeamScopedEntity & { id: number },
  deps: TeamScopedDetailPathDeps
): Promise<string> {
  const { workspaceSlug, teamSlug } = await resolveEntityWorkspaceAndTeamSlugs(
    organizationSlug,
    shortlist,
    deps.workspaces,
    deps.enterpriseService
  );

  if (workspaceSlug && teamSlug) {
    return appRouting
      .org(organizationSlug)
      .workspace(workspaceSlug)
      .team(teamSlug)
      .shortlists.detail(shortlist.id);
  }

  return myRouting
    .forWorkspace(organizationSlug)
    .shortlists()
    .detail(shortlist.id);
}

/**
 * Detail path for a callsheet from **entity assignment** (layer 3): its team's URL when the
 * catalogs name its workspace and team, else the organization URL (a global callsheet).
 *
 * For list rows and other entry points (a team callsheet on the organization list opens under
 * its team). Not for in-page chrome on an open callsheet — use `useAppCallsheetsRouting`.
 */
export function getCallsheetDetailPath(
  organizationSlug: string,
  callsheet: TeamScopedEntity & { id: number },
  workspaces: WorkspaceRef[] | undefined,
  teams: TeamRef[] | undefined
): string {
  const workspaceSlug = resolveEntityWorkspaceSlug(callsheet, workspaces);
  const teamSlug = resolveEntityTeamSlug(callsheet, teams);

  if (workspaceSlug && teamSlug) {
    return appRouting
      .org(organizationSlug)
      .workspace(workspaceSlug)
      .team(teamSlug)
      .callsheets.detail(callsheet.id);
  }

  return myRouting
    .forWorkspace(organizationSlug)
    .callsheets()
    .detail(callsheet.id);
}

/** {@link getCallsheetDetailPath} with the slugs fetched when the caller has no catalogs (after a write). */
export async function getCallsheetDetailPathAsync(
  organizationSlug: string,
  callsheet: TeamScopedEntity & { id: number },
  deps: TeamScopedDetailPathDeps
): Promise<string> {
  const { workspaceSlug, teamSlug } = await resolveEntityWorkspaceAndTeamSlugs(
    organizationSlug,
    callsheet,
    deps.workspaces,
    deps.enterpriseService
  );
  return getCallsheetDetailPath(
    organizationSlug,
    callsheet,
    workspaceSlug && callsheet.enterprise_workspace_id != null
      ? [{ id: callsheet.enterprise_workspace_id, slug: workspaceSlug }]
      : [],
    teamSlug && callsheet.enterprise_team_id != null
      ? [{ id: callsheet.enterprise_team_id, slug: teamSlug }]
      : []
  );
}

export function getSharedEnterpriseTeamId(
  entities: TeamScopedEntity[]
): number | null {
  if (entities.length === 0) {
    return null;
  }

  const teamIds = entities.map(entity => entity.enterprise_team_id ?? null);
  const definedIds = [
    ...new Set(teamIds.filter((id): id is number => id != null))
  ];

  if (definedIds.length === 0) {
    return null;
  }

  if (definedIds.length > 1) {
    return null;
  }

  const sharedTeamId = definedIds[0]!;
  if (teamIds.some(id => id != null && id !== sharedTeamId)) {
    return null;
  }

  if (teamIds.some(id => id === null)) {
    return null;
  }

  return sharedTeamId;
}
